import { parseUsd } from "../../shared/billing-money";
import type { BillingOverview, BillingTerms } from "../../shared/billing-schemas";
import {
  collectStripeInvoice,
  createStripeSubscription,
  grantStripeCredit,
  recordManualCredit,
  recordManualInvoice,
  recordManualSubscription,
} from "./billing-api";
import { dayToSeconds } from "./billing-format";
import { BillingFormError, REASON_REQUIREMENT, type FieldGuide } from "./billing-validation";

/**
 * The one form that turns approved terms into money (ADR-0073): which actions it offers, which
 * lines each may draw on, and how a typed draft becomes a request. Pure, apart from the request.
 */

export type CollectKind =
  | "stripe_invoice"
  | "stripe_subscription"
  | "stripe_credit"
  | "manual_invoice"
  | "manual_subscription"
  | "manual_credit";

export const COLLECT_KIND_LABEL: Record<CollectKind, string> = {
  stripe_invoice: "Collect one-time charges through Stripe",
  stripe_subscription: "Start a subscription through Stripe",
  stripe_credit: "Apply a credit to the Stripe balance",
  manual_invoice: "Record an invoice sent by hand or by another system",
  manual_subscription: "Record a subscription billed elsewhere",
  manual_credit: "Record a credit applied elsewhere",
};

export const COLLECT_SUBMIT_LABEL: Record<CollectKind, string> = {
  stripe_invoice: "Send invoice",
  stripe_subscription: "Start subscription",
  stripe_credit: "Apply credit",
  manual_invoice: "Record invoice",
  manual_subscription: "Record subscription",
  manual_credit: "Record credit",
};

export type CollectDraft = {
  kind: CollectKind;
  termsId: string;
  lineId: string;
  days: string;
  description: string;
  amount: string;
  reference: string;
  issued: string;
  due: string;
  paid: string;
  source: "manual" | "external";
  /** The go-live day a Stripe subscription first invoices; null until the employee chooses one. */
  goLive: string | null;
  /** ACH bank debit is always offered; a card only when this is ticked. */
  allowCard: boolean;
  reason: string;
};

const COLLECT_REQUIREMENTS: Record<string, string> = {
  terms: "Approved terms only; approve a draft first if the list is empty.",
  line: "The line of the terms this draws on.",
  days: "A whole number of days, 1 to 180. Stripe puts this due date on the invoice; a customer paying by bank needs 14 or more.",
  description: "2 to 200 characters, as the customer's invoice reads.",
  amount: "Dollars and cents, like 1250.00, with no more than two decimals.",
  reference: "Optional. Up to 120 characters: the other system's invoice or contract number.",
  issued: "The day the invoice went out, as a date.",
  due: "Optional. On or after the day it was issued.",
  paid: "Optional. Only if the money has already arrived.",
  goLive:
    "Optional. The go-live day the first invoice goes out, and each renewal after; leave it empty to invoice today.",
  reason: REASON_REQUIREMENT,
};

const SCHEMA_FIELD_NAMES: Record<string, string> = {
  termsId: "terms",
  termsLineId: "line",
  daysUntilDue: "days",
  firstInvoiceAt: "goLive",
  amountDueMinor: "amount",
  externalReference: "reference",
  issuedAt: "issued",
  startedAt: "issued",
  dueAt: "due",
  paidAt: "paid",
};

/** What each field of the collect form requires, in the words of the action being taken. */
export function collectGuide(kind: CollectKind): FieldGuide {
  return {
    rename: (field) => SCHEMA_FIELD_NAMES[field] ?? field,
    requirements:
      kind === "manual_subscription"
        ? { ...COLLECT_REQUIREMENTS, issued: "The day the arrangement began, as a date." }
        : COLLECT_REQUIREMENTS,
  };
}

function fieldError(field: string, message: string): BillingFormError {
  return new BillingFormError({ [field]: message });
}

/** Which actions the picker offers: what the employee may do, and what the deployment can do. */
export function offeredKinds(
  billing: BillingOverview,
  canManage: boolean,
  canCollect: boolean,
): CollectKind[] {
  const stripeReady = canCollect && billing.stripeConfigured && billing.customer !== null;
  return [
    ...(stripeReady ? (["stripe_invoice", "stripe_subscription", "stripe_credit"] as const) : []),
    ...(canManage ? (["manual_invoice", "manual_subscription", "manual_credit"] as const) : []),
  ];
}

export function linesFor(
  terms: BillingTerms | undefined,
  kind: CollectKind,
): BillingTerms["lines"] {
  if (!terms) return [];
  switch (kind) {
    case "stripe_subscription":
    case "manual_subscription":
      return terms.lines.filter((line) => line.kind === "recurring");
    case "stripe_credit":
    case "manual_credit":
      return terms.lines.filter((line) => line.kind === "credit" && line.totalMinor > 0);
    case "stripe_invoice":
      return terms.lines.filter((line) => line.kind === "one_time");
    default:
      return terms.lines.filter((line) => line.kind !== "credit");
  }
}

/** Whether the line picker may be left on "not one line" for this action. */
export function lineIsOptional(kind: CollectKind): boolean {
  return kind === "stripe_invoice" || kind === "manual_invoice";
}

function manualInvoiceRequest(
  customerSlug: string,
  draft: CollectDraft,
  termsLineId: string | null,
) {
  const amountDueMinor = parseUsd(draft.amount);
  if (amountDueMinor === null || amountDueMinor === 0)
    throw fieldError("amount", "Enter the exact amount due.");
  const issuedAt = dayToSeconds(draft.issued);
  if (issuedAt === null) throw fieldError("issued", "Enter the date the invoice was issued.");
  return recordManualInvoice(customerSlug, {
    termsId: draft.termsId,
    termsLineId,
    source: draft.source,
    description: draft.description.trim(),
    amountDueMinor,
    externalReference: draft.reference.trim() || null,
    issuedAt,
    dueAt: draft.due.trim() === "" ? null : dayToSeconds(draft.due),
    paidAt: draft.paid.trim() === "" ? null : dayToSeconds(draft.paid),
    reason: draft.reason,
  });
}

/** An empty "days until due" is a missing field, not zero days, on the two actions that ask for it. */
function daysUntilDueOf(draft: CollectDraft): number {
  const asksForDays = draft.kind === "stripe_invoice" || draft.kind === "stripe_subscription";
  if (asksForDays && draft.days.trim() === "") throw fieldError("days", "Required.");
  return Number(draft.days);
}

/** The go-live day as seconds, or null to invoice today. */
function goLiveOf(draft: CollectDraft): number | null {
  if (!draft.goLive) return null;
  const day = dayToSeconds(draft.goLive);
  if (day === null)
    throw fieldError("goLive", "Enter the go-live day as a date, or leave it empty.");
  return day;
}

/** Send the request the draft describes. Throws a `BillingFormError` naming the field when one is wrong. */
export async function submitCollectDraft(
  customerSlug: string,
  draft: CollectDraft,
  lines: BillingTerms["lines"],
): Promise<unknown> {
  const chosen = draft.lineId === "" ? null : draft.lineId;
  const line = chosen ?? lines[0]?.id ?? "";
  const daysUntilDue = daysUntilDueOf(draft);
  const { termsId, reason, allowCard } = draft;
  switch (draft.kind) {
    case "stripe_invoice":
      return collectStripeInvoice(customerSlug, {
        termsId,
        termsLineId: chosen,
        daysUntilDue,
        allowCard,
        reason,
      });
    case "stripe_subscription":
      return createStripeSubscription(customerSlug, {
        termsId,
        termsLineId: line,
        firstInvoiceAt: goLiveOf(draft),
        daysUntilDue,
        allowCard,
        reason,
      });
    case "stripe_credit":
      return grantStripeCredit(customerSlug, { termsId, termsLineId: line, reason });
    case "manual_credit":
      return recordManualCredit(customerSlug, { termsId, termsLineId: line, reason });
    case "manual_subscription": {
      const startedAt = dayToSeconds(draft.issued);
      if (startedAt === null)
        throw fieldError("issued", "Enter the date the subscription started.");
      return recordManualSubscription(customerSlug, {
        termsId,
        termsLineId: line,
        source: draft.source,
        externalReference: draft.reference.trim() || null,
        startedAt,
        reason,
      });
    }
    default:
      return manualInvoiceRequest(customerSlug, draft, chosen);
  }
}
